<script setup lang="ts">
import * as Sentry from '@sentry/vue';
import { getApostatudoApiUrl } from '~/utils/apostatudo/get-apostatudo-api-url';
import { getApostatudoRequestHeaders } from '~/utils/apostatudo/get-apostatudo-request-headers';

const SHORT_LINK_CODE = /^[A-Za-z0-9]+$/;
const REDIRECT_FALLBACK_DELAY_MS = 2000;

definePageMeta({ layout: 'blank' });

const route = useRoute();
const onelinkUrl = ref<string | null>(null);

onMounted(async () => {
  const code = route.params.code as string;

  if (SHORT_LINK_CODE.test(code)) {
    try {
      const coreOrigin = new URL(getApostatudoApiUrl()).origin;

      // O ÚNICO ponto que chama o core por `$fetch` e não por `fetch` cru — o helper
      // vale igual aqui (ENG-824). Foi ele que descartou o plugin embrulhando o `fetch`
      // global: o `ofetch` prende a referência na criação e escaparia do patch.
      const response = await $fetch<{ onelink_url?: string }>(
        `${coreOrigin}/api/traffic-tools/short-link/${code}`,
        {
          headers: {
            accept: 'application/json',
            ...getApostatudoRequestHeaders(),
          },
        },
      );

      if (typeof response?.onelink_url === 'string' && response.onelink_url) {
        window.location.href = response.onelink_url;

        // Se essa navegação realmente tirar o usuário daqui (o caso comum
        // em boa parte dos aparelhos), esse documento deixa de existir e o
        // timer abaixo nem chega a rodar — nada a fazer. Só nos casos em que
        // o app abre mas essa aba continua carregada em segundo plano (sem
        // navegar de verdade) é que, depois de um tempo, mostramos o link
        // manual como alternativa.
        setTimeout(() => {
          onelinkUrl.value = response.onelink_url as string;
        }, REDIRECT_FALLBACK_DELAY_MS);

        return;
      }
    } catch (error) {
      // 404 é o core dizendo que o código não existe — esperado, cai no
      // redirect pra home abaixo. Qualquer outro erro (rede, CORS, 5xx)
      // é inesperado — reportado ao Sentry pra não passar batido.
      const isUnknownCode =
        (error as { statusCode?: number })?.statusCode === 404;

      if (!isUnknownCode) {
        Sentry.captureException(error, {
          tags: { source: 'go.resolve-short-link' },
          extra: { code },
        });
      }
    }
  }

  navigateTo('/');
});
</script>

<template>
  <div
    class="min-h-[80vh] flex flex-col items-center justify-center gap-6 px-4 text-center"
  >
    <BaseCommonLogo
      always-logo
      class="h-8 w-auto"
      custom-size
    />

    <template v-if="onelinkUrl">
      <NuxtIcon
        class="text-4xl text-primary"
        name="external"
      />

      <div class="flex flex-col gap-1">
        <p class="text-lg font-bold text-texts">Quase lá!</p>
        <p class="text-sm text-texts/80">
          Se você não foi redirecionado automaticamente, toque no link abaixo
          para continuar.
        </p>
      </div>

      <a
        class="flex items-center justify-center gap-2 py-2 px-4 rounded-full bg-button-bg text-button-text font-bold text-sm"
        :href="onelinkUrl"
      >
        <NuxtIcon name="open" />
        Continuar
      </a>
    </template>
  </div>
</template>
